import { Button } from "@mui/material";
import { Dispatch, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Datagrid,
  FunctionField,
  List,
  RaRecord,
  ReferenceField,
  TextField,
  useListContext,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { HouseReviewStatus } from "../../enums/HouseReviewStatus";
import ApproveHouseReview from "../../interfaces/HouseReview/ApproveHouseReview";
import HouseReviewService from "../../services/HouseReviewService";
import ApproveHouseReviewDialog from "../Common/ApproveHouseReviewDialog";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import RejectHouseReviewDialog from "../Common/RejectHouseReviewDialog";
import CityService from "../../services/CityService";
import CityResponse from "../../interfaces/City/CityResponse";
import AreaService from "../../services/AreaService";
import CommonFilterCard, { CommonFilterField } from "../Common/CommonFilterCard";
import StyledDatagridWrapper, { commonDatagridSx } from "../Common/StyledDatagridWrapper";

const houseReviewService = new HouseReviewService();
const cityService = new CityService();
const areaService = new AreaService();

interface InputProps {
  setRejectHouseDialog: Dispatch<React.SetStateAction<boolean>>;
  setSelectedHouseReview: Dispatch<React.SetStateAction<RaRecord | undefined>>;
  setApproveDialog: Dispatch<React.SetStateAction<boolean>>;
}

// Filter Component
const HouseReviewFilterCard = () => {
  const { setFilters, filterValues } = useListContext();
  const [cities, setCities] = useState<CityResponse[]>([]);
  const [areas, setAreas] = useState<any>([]);
  const prevCityId = useRef(filterValues.cityId);

  // Fetch cities on component mount
  useEffect(() => {
    cityService.fetchAllCities(100, 0, {isCurrentlyServing:"YES"}, {}).then((res) => {
      setCities(res?.data || []);
    });
  }, []);

  // Fetch areas when city changes
  useEffect(() => {
    if (filterValues.cityId) {
      areaService
        .fetchAllAreas(1000, 0, { cityId: filterValues.cityId }, { field: "name", order: "ASC" })
        .then((res) => {
          setAreas(res?.data || []);
        });
    } else {
      setAreas([]); // Clear areas if no city is selected
    }
  }, [filterValues.cityId]);

  // When the selected city changes, drop the stale area filter
  // (same behavior as the previous handleCityChange)
  useEffect(() => {
    if (prevCityId.current !== filterValues.cityId) {
      prevCityId.current = filterValues.cityId;
      if (filterValues.areaId !== undefined) {
        const next: any = { ...filterValues };
        delete next.areaId;
        setFilters(next, [], false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues.cityId]);

  const filterFields: CommonFilterField[] = [
    {
      type: "select",
      label: "City",
      source: "cityId",
      options: cities.map((eachCity) => ({
        value: eachCity?.id,
        label: eachCity?.name,
      })),
    },
    {
      type: "select",
      label: "Area",
      source: "areaId",
      options: [
        ...areas.map((eachArea: any) => ({
          value: eachArea?.id,
          label: eachArea?.name,
        })),
        { value: "none", label: "none" },
      ],
    },
    {
      type: "select",
      label: "Status",
      source: "status",
      options: Object.values(HouseReviewStatus).map((key) => ({
        value: key,
        label: key,
      })),
    },
  ];

  return <CommonFilterCard fields={filterFields} />;
};

// Buttons Component
const CTAButtons = (props: InputProps) => {
  const record = useRecordContext();

  return (
    <div style={{ display: "flex", gap: 10 }}>
      {record.status === HouseReviewStatus.PENDING && (
        <>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              props.setApproveDialog(true);
              props.setSelectedHouseReview(record);
            }}
            disableElevation
          >
            Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => {
              props.setRejectHouseDialog(true);
              props.setSelectedHouseReview(record);
            }}
            disableElevation
          >
            Reject
          </Button>
        </>
      )}
    </div>
  );
};

// Main List Component
const HouseReviewList = () => {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedHouseReview, setSelectedHouseReview] = useState<RaRecord>();
  const [rejectHouseDialog, setRejectHouseDialog] = useState(false);
  const refresh = useRefresh();

  const handleApproveHouse = (data: ApproveHouseReview) => {
    if (selectedHouseReview) {
      houseReviewService
        .approve(selectedHouseReview.id as number, {
          localityId: data.localityId,
          cityId: data.cityId,
          areaId: data.areaId,
        })
        .then(refresh);
    }
  };

  return (
    <div>
      <ApproveHouseReviewDialog
        open={showApproveDialog}
        setOpen={setShowApproveDialog}
        callback={handleApproveHouse}
      />

      {rejectHouseDialog && (
        <RejectHouseReviewDialog
          data={selectedHouseReview as any}
          open={rejectHouseDialog}
          setOpen={setRejectHouseDialog}
        />
      )}

      <ConfirmationDialog />
      <List
        sort={{ field: "id", order: "DESC" }}
        sx={{
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
        }}
      >
        <>
        <HouseReviewFilterCard />

        <br />

        <StyledDatagridWrapper title="House Reviews">
        <Datagrid rowClick="show" bulkActionButtons={false} sx={commonDatagridSx}>
          <TextField source="id" />
          <ReferenceField source="houseId" link="show" reference="Houses">
            <TextField source="id" />
          </ReferenceField>
          <TextField source="status" />
          <ReferenceField source="cityId" link="show" reference="Cities">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="areaId" link="show" reference="Areas">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="localityId" link="show" reference="Localities">
            <TextField source="name" />
          </ReferenceField>
          <TextField source="otherSlc" />
          <FunctionField label="Created At" render={(record: any) => record?.createdAt ? moment(record.createdAt).format('DD MMM YYYY') : '-'} />
          <FunctionField label="Updated At" render={(record: any) => record?.updatedAt ? moment(record.updatedAt).format('DD MMM YYYY') : '-'} />
          <CTAButtons
            setRejectHouseDialog={setRejectHouseDialog}
            setApproveDialog={setShowApproveDialog}
            setSelectedHouseReview={setSelectedHouseReview}
          />
        </Datagrid>
        </StyledDatagridWrapper>
        </>
      </List>
    </div>
  );
};

export default HouseReviewList;
